import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import type { GallerySlide } from '../data/gallery';
interface ProductGalleryProps {
  heading: string;
  description?: string;
  caption?: string;
  slides: GallerySlide[];
  tone?: 'white' | 'gray';
}
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery({
  heading,
  description,
  caption,
  slides,
  tone = 'white'
}: ProductGalleryProps) {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = slides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + slides.length) % slides.length;
      return [next, step];
    });
  }, [slides.length]);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    if (!isZoomed) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsZoomed(false);
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isZoomed, paginate]);
  const onCarouselKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') paginate(1);
    if (event.key === 'ArrowLeft') paginate(-1);
  };
  return <section className={`py-20 px-4 ${tone === 'gray' ? 'bg-zinc-100' : 'bg-white'}`} data-id="element-211" data-mp="2uscztcr0oguj">
      <div className="max-w-5xl mx-auto" data-id="element-212" data-mp="2pztm8fsbxlpq">
        <div className="text-center mb-10" data-id="element-213" data-mp="2pys2lmx8zqwe">
          <h2 className="text-3xl md:text-4xl font-black uppercase mb-3 text-zinc-950" data-id="element-214" data-mp="22aih6m3y555s">{heading}</h2>
          {description && <p className="text-lg text-zinc-700 max-w-3xl mx-auto" data-id="element-215" data-mp="1xbfntyxbwndn">{description}</p>}
        </div>

        <div className="relative" onKeyDown={onCarouselKeyDown} data-id="element-216" data-mp="189kgqx19bap">
          <div className="relative bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600" role="group" aria-roledescription="carousel" aria-label={heading} tabIndex={0} data-id="element-217" data-mp="67vyv3olkxl3">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-218" data-mp="3egw4jqb3jp29">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${slides.length}: ${slide.title}`} data-id="element-219" data-mp="3jdw19am23fgp">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none" data-id="element-220" data-mp="29h7e9cjtw52n" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-violet-950/90 text-white flex items-center justify-center hover:bg-violet-950 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600" aria-label="View image larger" data-id="element-221" data-mp="1vh6czvznivmn">
              <Maximize2 size={18} data-id="element-222" data-mp="2ri2283fu4h8d" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-violet-600 text-white border border-violet-900 shadow-lg shadow-violet-950/30 flex items-center justify-center hover:bg-violet-700 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2" aria-label="Previous image" data-id="element-223" data-mp="2djls02irk8lh">
            <ChevronLeft size={24} data-id="element-224" data-mp="1yto3tmku3iwa" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-violet-600 text-white border border-violet-900 shadow-lg shadow-violet-950/30 flex items-center justify-center hover:bg-violet-700 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2" aria-label="Next image" data-id="element-225" data-mp="1ys35rmzb9hto">
            <ChevronRight size={24} data-id="element-226" data-mp="1b72al4pc5gnz" />
          </button>
        </div>

        <p className="text-center text-zinc-950 font-display font-bold text-sm sm:text-base mt-6" data-id="element-227" data-mp="1b5r95bjd4l01">
          {slide.title}
        </p>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-228" data-mp="1k80dtkry9k5i">
          {slides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${dotIndex === index ? 'w-8 bg-violet-600' : 'w-2.5 bg-zinc-300 hover:bg-zinc-400'}`} aria-label={`Go to image ${dotIndex + 1}: ${item.title}`} aria-current={dotIndex === index} data-id="element-229" data-mp="1ywwkpei9sr4j" data-mpi={dotIndex} />)}
        </div>

        {caption && <p className="text-center text-lg text-violet-900 font-semibold mt-8 max-w-3xl mx-auto" data-id="element-230" data-mp="2kwu8qu9p5fay">
            {caption}
          </p>}
      </div>

      <AnimatePresence data-id="element-231" data-mp="2ppnd3h196lmi">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-violet-950/95 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.title} onClick={() => setIsZoomed(false)} data-id="element-232" data-mp="2toe18ynqqsxh">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400" aria-label="Close enlarged image" data-id="element-233" data-mp="1e8nk0j7302rf">
              <X size={22} data-id="element-234" data-mp="3ds21gz6dh4xw" />
            </button>
            <motion.img key={slide.src} initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-235" data-mp="3dtd6f2bk0iql" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}